import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useCancelCompanyDeletion,
  useDataExportDownloadUrl,
  useDataRetentionStatus,
  useLatestDataExport,
  useRequestCompanyDeletion,
  useRequestDataExport,
} from '@/lib/hooks/data-retention'

function formatDate(value: string | Date | null | undefined): string | null {
  if (!value) return null
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatSize(bytes: number | null | undefined): string {
  if (!bytes) return ''
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? ` (${mb.toFixed(1)} MB)` : ` (${Math.ceil(bytes / 1024)} KB)`
}

// Owner-only: export the company's data and files, and delete the company.
// Lives on the subscription page because lapsed owners are confined to it.
export function CompanyDataCard() {
  const status = useDataRetentionStatus()
  const latestExport = useLatestDataExport()
  const requestExport = useRequestDataExport()
  const downloadUrl = useDataExportDownloadUrl()
  const requestDeletion = useRequestCompanyDeletion()
  const cancelDeletion = useCancelCompanyDeletion()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')

  const company = status.data
  const exportRow = latestExport.data
  const building = exportRow?.status === 'pending' || requestExport.isPending
  const ownerRequested = company?.deletionReason === 'owner_request'

  const download = () => {
    if (!exportRow) return
    downloadUrl.mutate(
      { id: exportRow.id },
      { onSuccess: ({ url }) => window.open(url, '_blank') }
    )
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold text-content-primary-bold tracking-[0.07px]">
          Your data
        </p>
        {ownerRequested ? (
          <p className="text-sm text-content-negative-bold tracking-[0.07px]">
            You asked us to delete this company. All data and files will be permanently deleted
            within 24 hours.
          </p>
        ) : (
          <p className="text-sm text-content-primary-subtle tracking-[0.07px]">
            Download everything in your company account — records as CSV and JSON, plus all uploaded
            documents and photos — as one ZIP file.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {exportRow?.status === 'ready' ? (
          <Button variant="primary" className="bg-background-brand-bold" loading={downloadUrl.isPending} onClick={download}>
            Download export{formatSize(exportRow.sizeBytes)}
          </Button>
        ) : (
          <Button
            variant="primary"
            className="bg-background-brand-bold"
            loading={building}
            disabled={building}
            onClick={() => requestExport.mutate({})}
          >
            {building ? 'Preparing export…' : 'Export data'}
          </Button>
        )}
        <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
          {exportRow?.status === 'ready'
            ? `Ready since ${formatDate(exportRow.createdAt)}. Available until ${formatDate(exportRow.expiresAt)}.`
            : exportRow?.status === 'failed'
              ? 'The last export failed. Please try again.'
              : building
                ? 'This can take a few minutes. You can leave this page and come back.'
                : 'Exports stay available for 7 days.'}
        </p>
        {exportRow?.status === 'ready' && (
          <Button variant="outline" disabled={building} onClick={() => requestExport.mutate({})}>
            Create a new export
          </Button>
        )}
      </div>

      <div className="h-px bg-border" />

      {ownerRequested ? (
        <Button variant="outline" loading={cancelDeletion.isPending} onClick={() => cancelDeletion.mutate({})}>
          Cancel deletion request
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
            Delete company and all data
          </Button>
          <p className="text-xs text-content-primary-subtle tracking-[0.07px]">
            Cancel your subscription first — deleting the company doesn't stop billing.
          </p>
        </div>
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open)
          if (!open) {
            setConfirmName('')
            requestDeletion.reset()
          }
        }}
      >
        <AlertDialogContent className="p-6 flex flex-col gap-4">
          <AlertDialogTitle>Delete {company?.name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-content-primary-subtle">
            All crew, aircraft, trips, documents, photos and messages will be permanently deleted
            within 24 hours, and crew who don't belong to another company lose their login. This
            can't be undone. Type the company name to confirm.
          </AlertDialogDescription>
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={company?.name}
            autoComplete="off"
          />
          {requestDeletion.error && (
            <p className="text-xs font-semibold text-content-negative-bold">{requestDeletion.error.message}</p>
          )}
          <div className="flex flex-col gap-2">
            <Button
              variant="destructive"
              disabled={!company || confirmName.trim() !== company.name.trim()}
              loading={requestDeletion.isPending}
              onClick={() =>
                requestDeletion.mutate({ confirmName }, { onSuccess: () => setConfirmOpen(false) })
              }
            >
              Delete permanently
            </Button>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Keep company
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
